// ==============================|| DOCHÁZKA API ||============================== //
// První slice modulu Docházka (2026-08-23, schválená specifikace "Docházkový
// systém") - jen clock in/out zatím, žádosti o nepřítomnost/měsíční uzávěrka
// jsou samostatná, zatím nepostavená práce. Session-gated uvnitř
// AttendanceControlleru (jakákoliv role na module_key 'attendance').

import { notifySessionExpired } from 'utils/sessionExpired';

async function fetchCsrfToken() {
  const res = await fetch('/auth/csrf-token', { credentials: 'include' });
  const body = await res.json();
  return body?.data?.csrf_token;
}

async function apiGet(path) {
  const res = await fetch(path, { credentials: 'include' });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body.success === false) {
    if (res.status === 401) notifySessionExpired();
    const error = new Error(body.message || 'Požadavek se nezdařil.');
    error.code = body.code;
    throw error;
  }
  return body.data;
}

async function apiPost(path, payload) {
  const csrfToken = await fetchCsrfToken();
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
    body: JSON.stringify(payload)
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body.success === false) {
    if (res.status === 401) notifySessionExpired();
    const error = new Error(body.message || 'Požadavek se nezdařil.');
    error.code = body.code;
    throw error;
  }
  return body.data;
}

export const getAttendanceState = () => apiGet('/attendance/state');

export const getAttendanceHistory = (from, to) => {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const qs = params.toString();
  return apiGet(`/attendance/history${qs ? `?${qs}` : ''}`);
};

// Posledních N záznamů bez ohledu na datum (dashboard widget), na rozdíl od
// getAttendanceHistory() výše, který je vázaný na měsíc.
export const getRecentAttendanceHistory = (limit = 5) => apiGet(`/attendance/history/recent?limit=${limit}`);

// Docházková karta (2026-08-30, na výslovné přání uživatele) - jen měsíce
// s reálnými daty (pro záložky rok/měsíc) + samotná měsíční tabulka.
export const getMyMonthlyCardMonths = () => apiGet('/attendance/monthly-card/months');
export const getMyMonthlyCard = (year, month) => apiGet(`/attendance/monthly-card?year=${year}&month=${month}`);

// "Kontrola záznamů docházky" (2026-09-01) - stejné záložky rok/měsíc jako
// docházková karta výše (getMyMonthlyCardMonths se znovupoužívá beze
// změny), jen jiný obsah - plochý seznam zápisů od nejnovějšího.
export const getMyActivity = (year, month) => apiGet(`/attendance/activity?year=${year}&month=${month}`);

// eventType: 'clock_in' | 'clock_out' (nepovinné) - omezí nabídku jen na
// důvody platné pro tuhle konkrétní akci (viz applies_to níže).
export const getVerificationReasons = (eventType) =>
  apiGet(`/attendance/verification-reasons${eventType ? `?event_type=${eventType}` : ''}`);

// Admin CRUD nad katalogem ověřovacích důvodů (2026-08-26, real bug fix -
// katalog byl trvale prázdný, appka na něj neměla admin UI) - nikdy
// hard-delete, jen is_active toggle. appliesTo: 'both' | 'clock_in' | 'clock_out'.

export const listVerificationReasonsAdmin = () => apiGet('/attendance/verification-reasons/admin');

export const createVerificationReason = (label, sortOrder, icon, appliesTo) =>
  apiPost('/attendance/verification-reasons', { label, sort_order: sortOrder, icon, applies_to: appliesTo });

export const updateVerificationReason = (id, label, sortOrder, icon, appliesTo) =>
  apiPost(`/attendance/verification-reasons/${id}`, { label, sort_order: sortOrder, icon, applies_to: appliesTo });

export const setVerificationReasonActive = (id, active) => apiPost(`/attendance/verification-reasons/${id}/set-active`, { active });

// context: 'standard' (hlavní přepínač, appka sama pozná příchod/odchod) nebo
// 'outside_hours' (musí se poslat explicitní eventType 'clock_in'/'clock_out').
export const clockAttendance = (context, eventType, extra = {}) =>
  apiPost('/attendance/clock', { context, event_type: eventType, ...extra });

export const listAttendanceAreas = () => apiGet('/attendance/areas');

export const createAttendanceArea = (name, points) => apiPost('/attendance/areas', { name, points });

export const setAttendanceAreaActive = (id, active) => apiPost(`/attendance/areas/${id}/set-active`, { active });

// Žádosti o nepřítomnost (§6.1 schválené specifikace, 2026-08-23).

export const listMyLeavePeriods = () => apiGet('/attendance/leave-periods');

// leaveType: 'vacation' | 'unpaid_leave' - vrací { id, collisions } (kolize
// jsou jen informativní, nic neblokují). totalHours (2026-09-02, na
// výslovné přání uživatele - "u dovolené nejsou denní či půldenní, musí to
// být vše v hodinách") je POVINNÝ pro 'vacation' (appka nemá zvlášť
// denní/půldenní koncept, jen počet hodin za celý rozsah od-do), pro
// 'unpaid_leave' se ignoruje.
export const requestLeavePeriod = (leaveType, startDate, endDate, comment, totalHours) =>
  apiPost('/attendance/leave-periods', {
    leave_type: leaveType,
    start_date: startDate,
    end_date: endDate,
    comment,
    total_hours: totalHours
  });

// leaveType: 'family_care' - fakt, ne žádost, rovnou 'schvaleno'. Nemoc
// (sick_leave/DPN) SEM NEPATŘÍ - zapisuje ji vedoucí nebo mzdová na základě
// datovky, viz recordSickLeaveForUser() níže. 'doctor_note' SEM TAKY
// NEPATŘÍ - nahrazeno propustkou (viz listPropustkaReasons()/
// createPropustka() níže, real domain fix 2026-08-23 - viz
// AttendancePropustkaService's docblock, proč to není leave_period).
// careCodeStart je u family_care POVINNÝ (SMS kód od lékaře pro mzdovou).
export const recordInformationalLeave = (leaveType, startDate, endDate, comment, careCodeStart) =>
  apiPost('/attendance/leave-periods/informational', {
    leave_type: leaveType,
    start_date: startDate,
    end_date: endDate,
    comment,
    care_code_start: careCodeStart
  });

// Ukončení OČR - zaměstnanec sám, s POVINNÝM ukončovacím SMS kódem
// (samostatná žádost lékaři, samostatná SMS).
export const closeOpenLeavePeriod = (endDate, careCodeEnd) =>
  apiPost('/attendance/leave-periods/close-open', { end_date: endDate, care_code_end: careCodeEnd });

// Dodatečný zápis schválené dovolené/neplaceného volna za zaměstnance
// (2026-09-04) - manažer/mzdová "dvířka" pro případy, které se nepovedlo
// dotáhnout elektronicky přes zaměstnance; komentář je POVINNÝ, totalHours
// POVINNÝ pro 'vacation' (viz requestLeavePeriod() výše).
export const recordApprovedLeaveForEmployee = (userId, leaveType, startDate, endDate, comment, totalHours) =>
  apiPost('/attendance/leave-periods/record-approved-for-employee', {
    user_id: userId,
    leave_type: leaveType,
    start_date: startDate,
    end_date: endDate,
    comment,
    total_hours: totalHours
  });

// Externí dodavatel jen NAHLÁSÍ volno (2026-08-30, na výslovné přání
// uživatele) - fakt, rovnou 'schvaleno', appka nic neschvaluje ani
// neblokuje (doporučených 14 dní dopředu appka jen doporučí, nevynucuje -
// viz AttendanceLeavePeriodService::recordContractorNotice()).
export const recordContractorNotice = (startDate, endDate, comment) =>
  apiPost('/attendance/leave-periods/contractor-notice', { start_date: startDate, end_date: endDate, comment });

// Nemoc (DPN) - zapisuje/ukončuje vedoucí nebo mzdová PRO zaměstnance
// (userId), na základě datovky. NIKDY sám zaměstnanec.
export const recordSickLeaveForUser = (userId, startDate, endDate, comment) =>
  apiPost('/attendance/leave-periods/sick-leave', { user_id: userId, start_date: startDate, end_date: endDate, comment });

export const closeSickLeaveForUser = (userId, endDate) =>
  apiPost('/attendance/leave-periods/sick-leave/close', { user_id: userId, end_date: endDate });

// Přehled otevřených (nedokončených) DPN v manažerově rozsahu (2026-09-02,
// na výslovné přání uživatele - "zajistit, že musí nadřízený nebo mzdová
// tu neschopenku ukončit") - bez tohohle appka na zapomenutou otevřenou
// nemoc nijak neupozorní.
export const listOpenSickLeaves = () => apiGet('/attendance/leave-periods/sick-leave/open');

// Přehled otevřených (nedokončených) OČR v manažerově rozsahu (2026-09-02,
// na výslovné přání uživatele - "opět zavést mechanismus do docházky") -
// čistě informativní, appka OČR manažerovi neuzavírá (self-service).
export const listOpenFamilyCare = () => apiGet('/attendance/leave-periods/family-care/open');

// OČR bez SMS kódu na základě papírového tiskopisu (2026-09-02, na
// výslovné přání uživatele - "Ano můžeš zavést pro mzdovou účetní, pro
// nikoho dalšího ne", potvrzeno "myslím to obcházení SMS kódu") - appka
// tyhle dvě akce na backendu vynucuje výhradně pro mzdovou účetní
// (isPayrollGrant()), ne pro oblastní manažera, i když drží stejnou min.
// roli regional_manager.
export const recordFamilyCareFromPaper = (userId, startDate, endDate, comment) =>
  apiPost('/attendance/leave-periods/family-care/record-from-paper', {
    user_id: userId,
    start_date: startDate,
    end_date: endDate,
    comment
  });

export const closeFamilyCareFromPaper = (userId, endDate) =>
  apiPost('/attendance/leave-periods/family-care/close-from-paper', { user_id: userId, end_date: endDate });

export const listPendingLeavePeriods = () => apiGet('/attendance/leave-periods/pending');

export const listDecidedLeavePeriods = () => apiGet('/attendance/leave-periods/decided');

export const startReviewLeavePeriod = (id) => apiPost(`/attendance/leave-periods/${id}/start-review`, {});

export const decideLeavePeriod = (id, approve, note) => apiPost(`/attendance/leave-periods/${id}/decide`, { approve, note });

// "Opravuji záznamy" - úprava už schválené dovolené/NV (§4.5), s povinným
// důvodem úpravy (auditní stopa).
export const editLeavePeriod = (id, startDate, endDate, reason) =>
  apiPost(`/attendance/leave-periods/${id}/edit`, { start_date: startDate, end_date: endDate, reason });

// §208 překážka v práci - manažer zakládá PRO zaměstnance (userId).
export const createObstacle = (userId, startDate, endDate, comment) =>
  apiPost('/attendance/leave-periods/obstacle', { user_id: userId, start_date: startDate, end_date: endDate, comment });

// Zaměstnanec podepisuje překážku, kterou pro něj založil manažer - stejný
// PIN+kresba mechanismus jako obecný /signatures/capture, jen vlastní
// endpoint (AttendanceLeavePeriodService::signObstacle() zapíše signature_id
// a rozhodnutí atomicky v jednom kroku).
export const signLeavePeriod = (id, pin, signatureImage) =>
  apiPost(`/attendance/leave-periods/${id}/sign`, { pin, signature_image: signatureImage });

// Propustka (2026-08-23) - jednodenní, uprostřed směny odchod/návrat z
// důvodu z ceníku (placeno/neplaceno per důvod, ne per instance). Vlastní
// koncept, NENÍ attendance_leave_periods - viz AttendancePropustkaService.

// Seznam důvodů se cachuje v paměti modulu (2026-08-31, na výslovné přání
// uživatele - "načíst to s menu") - AttendanceAccordion.jsx ho volá znovu
// při každém otevření profilového panelu (MUI Drawer odmountuje/namountuje
// obsah), takže bez cache byl vidět krátký záblesk prázdného seznamu, než
// se stihla dokončit nová síťová žádost. Profile/index.jsx navíc tenhle
// seznam prefetchuje rovnou při načtení hlavičky (ne až při otevření
// Draweru), takže ve většině případů je odpověď z cache okamžitá.
// Invalidace při create/update/set-active níže, aby manažer po úpravě
// katalogu neviděl ve stejné session zastaralá data.
let _propustkaReasonsCache = null;

function clearPropustkaReasonsCache() {
  _propustkaReasonsCache = null;
}

export const listPropustkaReasons = () => {
  if (!_propustkaReasonsCache) {
    _propustkaReasonsCache = apiGet('/attendance/propustky/reasons').catch((e) => {
      _propustkaReasonsCache = null;
      throw e;
    });
  }
  return _propustkaReasonsCache;
};

export const listMyPropustky = () => apiGet('/attendance/propustky');

export const createPropustka = (reasonId, propustkaDate, departureTime, returnTime, comment, pendingClassification) =>
  apiPost('/attendance/propustky', {
    reason_id: reasonId,
    propustka_date: propustkaDate,
    departure_time: departureTime,
    return_time: returnTime,
    comment,
    pending_classification: pendingClassification
  });

export const markPropustkaReturned = (returnTime) => apiPost('/attendance/propustky/mark-returned', { return_time: returnTime });

// Dodatečný zápis propustky za zaměstnance (2026-09-04) - manažer/mzdová
// "dvířka" pro případy, které se nepovedlo dotáhnout elektronicky přes
// zaměstnance; komentář (důvod dodatečného zápisu) je POVINNÝ.
export const recordPropustkaForEmployee = (userId, reasonId, propustkaDate, departureTime, returnTime, comment) =>
  apiPost('/attendance/propustky/record-for-employee', {
    user_id: userId,
    reason_id: reasonId,
    propustka_date: propustkaDate,
    departure_time: departureTime,
    return_time: returnTime,
    comment
  });

// "Neplánovaná zdravotní absence" (2026-09-01, Technická dokumentace bod 5) -
// propustky čekající na doložení (pending_classification=1), manažerské.
export const listPendingPropustky = () => apiGet('/attendance/propustky/pending');

// "Živý přehled kdo je na propustce" (2026-09-01, Technická dokumentace bod 4).
export const listOpenPropustky = () => apiGet('/attendance/propustky/open');
export const resolvePendingPropustka = (id, resolvedAs, sickLeaveEnd, comment) =>
  apiPost(`/attendance/propustky/${id}/resolve`, { resolved_as: resolvedAs, sick_leave_end: sickLeaveEnd, comment });

// Kompletní historie propustek pro tým (2026-09-04) - manažerský/mzdový
// přehled s náhledem stavu doložení (has_attachment), viz
// AttendancePropustkaController::team(). from/to jsou volitelné YYYY-MM-DD.
export const listTeamPropustky = (from, to) => {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const qs = params.toString();
  return apiGet(`/attendance/propustky/team${qs ? `?${qs}` : ''}`);
};

// "Dnešní stav týmu" (2026-09-02) - manažerský dashboard, viz
// AttendanceTeamOverviewService.
export const fetchTeamTodayOverview = () => apiGet('/attendance/manager/today-overview');

// "Týdenní kapacita" (2026-09-02) - Po-dnes headcount per job_title vs. minimum, viz
// AttendanceTeamOverviewService::weeklyCapacity().
export const fetchWeeklyCapacity = () => apiGet('/attendance/manager/weekly-capacity');

// Měsíční export pro mzdy (2026-09-01) - mzdová účetní/superadmin.
export const getPayrollExport = (year, month) => apiGet(`/attendance/payroll-export?year=${year}&month=${month}`);

// "Dokladování" (2026-09-01) - foto/scan potvrzení k VLASTNÍ propustce
// (data: URL, viz AttendancePropustkaService::validateAttachmentImage()).
// GET smí i mzdová účetní/superadmin pro cizí propustku (attendance_payroll_access) - backend to ověří samo.
export const uploadPropustkaAttachment = (id, dataUrl) => apiPost(`/attendance/propustky/${id}/attachment`, { attachment: dataUrl });
export const getPropustkaAttachment = (id) => apiGet(`/attendance/propustky/${id}/attachment`);

// "Žádanka na propustku" (2026-09-01) - plánovaný odchod čekající na
// schválení nadřízeným (AttendancePropustkaRequestController). Self-service
// pro zaměstnance, schvalovací akce jsou manažerské (min. regional_manager,
// vynuceno na backendu).
export const listMyPropustkaRequests = () => apiGet('/attendance/propustky/requests');

export const createPropustkaRequest = (reasonId, requestedDate, requestedDepartureTime, requestedReturnTime, comment) =>
  apiPost('/attendance/propustky/requests', {
    reason_id: reasonId,
    requested_date: requestedDate,
    requested_departure_time: requestedDepartureTime,
    requested_return_time: requestedReturnTime,
    comment
  });

export const listPendingPropustkaRequests = () => apiGet('/attendance/propustky/requests/pending');
export const listDecidedPropustkaRequests = () => apiGet('/attendance/propustky/requests/decided');
export const startReviewPropustkaRequest = (id) => apiPost(`/attendance/propustky/requests/${id}/start-review`, {});
export const decidePropustkaRequest = (id, approve, note) => apiPost(`/attendance/propustky/requests/${id}/decide`, { approve, note });

// Admin CRUD nad katalogem důvodů propustek (2026-08-23) - nikdy hard-delete,
// jen is_active toggle (viz migrace 045's docblock).

export const listPropustkaReasonsAdmin = () => apiGet('/attendance/propustky/reasons/admin');

export const createPropustkaReason = (label, legalReference, wageCategory, icon, sortOrder) =>
  apiPost('/attendance/propustky/reasons', {
    label,
    legal_reference: legalReference,
    wage_category: wageCategory,
    icon,
    sort_order: sortOrder
  }).then((r) => {
    clearPropustkaReasonsCache();
    return r;
  });

export const updatePropustkaReason = (id, label, legalReference, wageCategory, icon, sortOrder) =>
  apiPost(`/attendance/propustky/reasons/${id}`, {
    label,
    legal_reference: legalReference,
    wage_category: wageCategory,
    icon,
    sort_order: sortOrder
  }).then((r) => {
    clearPropustkaReasonsCache();
    return r;
  });

export const setPropustkaReasonActive = (id, active) =>
  apiPost(`/attendance/propustky/reasons/${id}/set-active`, { active }).then((r) => {
    clearPropustkaReasonsCache();
    return r;
  });

// Měsíční uzávěrka (§6.2, přepsáno 2026-08-30) - vedoucí podepíše jako
// první (PIN+kresba, rovnou uzamyká) → zaměstnanec podepíše (appka vygeneruje
// PDF a pošle ho na potvrzený soukromý e-mail) nebo namítne (rovnou
// nesrovnalost, bez podpisu) → mzdová/manažer může i po podpisu zaměstnance
// nahlásit nesrovnalost → correct() zvouduje starý cyklus, znovu čeká na
// podpis vedoucího.

export const listMyMonthlyApprovals = () => apiGet('/attendance/monthly-approvals');

export const signMonthlyApproval = (year, month, pin, signatureImage) =>
  apiPost('/attendance/monthly-approvals/sign', { year, month, pin, signature_image: signatureImage });

export const objectMonthlyApproval = (year, month, note) => apiPost('/attendance/monthly-approvals/object', { year, month, note });

export const listPendingMonthlySignatures = () => apiGet('/attendance/monthly-approvals/pending-signature');

export const listMonthlyDiscrepancies = () => apiGet('/attendance/monthly-approvals/discrepancies');

export const getMonthlyApprovalHistory = (userId, year, month) =>
  apiGet(`/attendance/monthly-approvals/history?user_id=${userId}&year=${year}&month=${month}`);

export const signAsManagerMonthlyApproval = (userId, year, month, pin, signatureImage) =>
  apiPost('/attendance/monthly-approvals/sign-as-manager', { user_id: userId, year, month, pin, signature_image: signatureImage });

export const reportMonthlyDiscrepancy = (userId, year, month, note) =>
  apiPost('/attendance/monthly-approvals/discrepancy', { user_id: userId, year, month, note });

export const correctMonthlyApproval = (userId, year, month, note) =>
  apiPost('/attendance/monthly-approvals/correct', { user_id: userId, year, month, note });

// Kompletní výpis zaměstnanců + "kolize" za měsíc (2026-08-30, na výslovné
// přání uživatele - "Uzávěrka docházky... výpis zaměstnanců jako
// accordion... pokud jsou kolize ty budou v body").
export const getMonthlyApprovalsRoster = (year, month) => apiGet(`/attendance/monthly-approvals/roster?year=${year}&month=${month}`);

export const resolveMonthlyApprovalCollision = (userId, type, collisionId) =>
  apiPost('/attendance/monthly-approvals/resolve-collision', { user_id: userId, type, collision_id: collisionId });

// 3-cestné rozhodnutí nad kolizí (2026-09-04) - decision: 'approved' |
// 'approved_with_reservation' | 'rejected'. Komentář je POVINNÝ mimo
// 'approved' (u výhrady jde do centra zpráv zaměstnanci, u zamítnutí je to
// důvod, proč se kolize dál nevyřešila).
export const reviewMonthlyApprovalCollision = (userId, type, collisionId, decision, comment) =>
  apiPost('/attendance/monthly-approvals/review-collision', { user_id: userId, type, collision_id: collisionId, decision, comment });

// Přímá oprava jednoho řádku docházky (typ 'event') z uzávěrky (2026-09-04) -
// zaměstnanec si sám opravit nemůže, jen nadřízený/mzdová; komentář (důvod
// opravy) je POVINNÝ, zaměstnanci přijde zpráva o změně stavu docházky.
export const correctMonthlyApprovalEvent = (userId, eventId, occurredAt, comment) =>
  apiPost('/attendance/monthly-approvals/correct-event', { user_id: userId, event_id: eventId, occurred_at: occurredAt, comment });

// Chybějící dny (2026-08-23) - odvozené, ne uložené (viz
// AttendanceDayAcknowledgementService's docblock); acknowledge() jen
// zapisuje VYŘEŠENÍ konkrétního dne.

export const getMissingDays = (userId, year, month) =>
  apiGet(`/attendance/day-acknowledgements/missing?user_id=${userId}&year=${year}&month=${month}`);

export const acknowledgeDay = (userId, day, comment) => apiPost('/attendance/day-acknowledgements', { user_id: userId, day, comment });

// Nárok na dovolenou (2026-08-26) - čerpáno/zbývá se vždy dopočítává na
// backendu ze schválených dovolených, tady se jen čte hotový souhrn.
// V HODINÁCH, ne ve dnech (2026-08-30, na výslovné přání uživatele - "dle
// nového nařízení se musí zadávat dovolená v hodinách").

export const getMyLeaveEntitlement = (year) => apiGet(`/attendance/leave-entitlements/me?year=${year}`);

// Kompletní výpis zaměstnanců v manažerově rozsahu, podle abecedy - přijmení
// (2026-08-30, na výslovné přání uživatele).
export const getLeaveEntitlementsRoster = (year) => apiGet(`/attendance/leave-entitlements/roster?year=${year}`);

export const getLeaveEntitlement = (userId, year) => apiGet(`/attendance/leave-entitlements?user_id=${userId}&year=${year}`);

export const setLeaveEntitlement = (userId, year, entitlementHours, note) =>
  apiPost('/attendance/leave-entitlements', { user_id: userId, year, entitlement_hours: entitlementHours, note });

// Nástup/odchod uprostřed měsíce + úvazek (2026-08-23) - jeden řádek na
// uživatele (upsert), viz AttendanceEmploymentPeriodRepository's docblock.

// Kompletní výpis zaměstnanců v manažerově rozsahu, podle abecedy - přijmení
// (2026-08-30, na výslovné přání uživatele - "stránka bude ve stylu Nárok
// na dovolenou").
export const getEmploymentPeriodsRoster = () => apiGet('/attendance/employment-periods/roster');

export const getEmploymentPeriod = (userId) => apiGet(`/attendance/employment-periods?user_id=${userId}`);

export const setEmploymentPeriod = (
  userId,
  activeFrom,
  activeUntil,
  employmentFraction,
  shiftStartTime,
  shiftEndTime,
  breakDurationMinutes,
  breakTrackingRequired,
  reason
) =>
  apiPost('/attendance/employment-periods', {
    user_id: userId,
    active_from: activeFrom,
    active_until: activeUntil,
    employment_fraction: employmentFraction,
    shift_start_time: shiftStartTime,
    shift_end_time: shiftEndTime,
    break_duration_minutes: breakDurationMinutes,
    break_tracking_required: breakTrackingRequired,
    reason
  });

// "Přestávka" (2026-09-02) - self-service evidence, server-time only (viz
// AttendanceBreakService's vlastní docblock). "Dodržuje" (break_tracking_
// required=true na Úvazku zaměstnance) zaměstnanci si ji evidují sami přes
// tyhle dva endpointy; "toleruje" appka nikdy nevyzve k zápisu (nominál se
// odečítá automaticky, viz monthlyCard()).
export const getBreakState = () => apiGet('/attendance/breaks/state');
export const startBreak = () => apiPost('/attendance/breaks/start', {});
export const endBreak = () => apiPost('/attendance/breaks/end', {});

// Katalog "typů úvazku" (2026-09-01, na výslovné přání uživatele) - délka
// úvazku jako DB-backed katalog místo natvrdo zapsaných hodnot ve
// frontendu. Admin CRUD (create/update/setActive) je omezené na mzdovou
// účetní/superadmina na backendu (ATT-403-006).
export const listEmploymentTypes = () => apiGet('/attendance/employment-periods/types');
export const listEmploymentTypesAdmin = () => apiGet('/attendance/employment-periods/types/admin');
export const createEmploymentType = (label, fraction, sortOrder) =>
  apiPost('/attendance/employment-periods/types', { label, fraction, sort_order: sortOrder });
export const updateEmploymentType = (id, label, fraction, sortOrder) =>
  apiPost(`/attendance/employment-periods/types/${id}`, { label, fraction, sort_order: sortOrder });
export const setEmploymentTypeActive = (id, isActive) =>
  apiPost(`/attendance/employment-periods/types/${id}/active`, { is_active: isActive });

// Minimální obsazenost funkce (2026-08-23, §4.4) - job_title je volný text
// (žádný samostatný katalog funkcí v appce), jeden řádek na funkci (upsert).

export const listJobTitleMinimums = () => apiGet('/attendance/job-title-minimums');

export const setJobTitleMinimum = (jobTitle, minimumCount) =>
  apiPost('/attendance/job-title-minimums', { job_title: jobTitle, minimum_count: minimumCount });

export const removeJobTitleMinimum = (id) => apiPost(`/attendance/job-title-minimums/${id}/delete`, {});
